import { Box, FormikSelectInputNative, SelectInput } from '@palmetto/palmetto-components';
import { Field, useFormikContext } from 'formik';
import { useCallback, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { ProgramType } from 'types';

import { percentageFormatter } from '../../../../helpers/percentageFormatter';
import { useGetPriceSheetsQuery, useLazyGetPriceSheetDetailsQuery } from '../../../../services/pricing';

export interface ConsumerSolarPricingFormValues {
  priceSheet: string;
  escalationRateMin: string;
  escalationRateMax: string;
  minSystemSize: string;
  maxSystemSize: string;
  minProductionFactor: string;
  maxProductionFactor: string;
}

export const BulkEditConsumerSolarPricingFormSection = () => {
  const { setFieldValue, isSubmitting, values, touched, errors, handleBlur } =
    useFormikContext<ConsumerSolarPricingFormValues>();
  const { programType } = useParams<{ programType: string }>();
  const { priceSheet } = values;
  const { data: priceSheetResults } = useGetPriceSheetsQuery({
    pageNum: 1,
    pageSize: 1000,
    programType: programType as ProgramType,
  });
  const [trigger, { data: priceSheetDetails = {} }] = useLazyGetPriceSheetDetailsQuery();

  useEffect(() => {
    if (priceSheetResults?.data?.length && priceSheet) {
      trigger({ id: priceSheet });
    }
  }, [priceSheet, priceSheetResults?.data, trigger]);

  const priceSheetOptions = useMemo(
    () =>
      priceSheetResults?.data?.map((priceSheet) => ({
        label: priceSheet.name,
        value: priceSheet.id,
      })) ?? [],
    [priceSheetResults?.data],
  );

  const escRateOptions = useMemo(() => {
    const escRates = new Set();
    priceSheetDetails?.prices?.forEach((p: any) => {
      escRates.add(p.escalationRate);
    });
    return Array.from(escRates)
      .toSorted((a, b) => Number(a) - Number(b))
      .map((rate: any) => ({ label: percentageFormatter.format(rate), value: rate }));
  }, [priceSheetDetails?.prices]);

  const systemSizeOptions = useMemo(() => {
    const sizes = new Set();
    priceSheetDetails?.prices?.forEach((p: any) => {
      sizes.add(p.minSystemSize);
      sizes.add(p.maxSystemSize);
    });
    return Array.from(sizes)
      .toSorted((a: any, b: any) => a - b)
      .map((size: any) => ({ label: size, value: size }));
  }, [priceSheetDetails?.prices]);

  const productionOptions = useMemo(() => {
    const productions = new Set();
    priceSheetDetails?.prices?.forEach((p: any) => {
      productions.add(p.minProductionFactor);
      productions.add(p.maxProductionFactor);
    });
    return Array.from(productions)
      .toSorted((a: any, b: any) => a - b)
      .map((prod: any) => ({ label: prod, value: prod }));
  }, [priceSheetDetails?.prices]);

  const handlePriceSheetSelection = useCallback(
    (event: any) => {
      const id = event?.target?.value?.value ?? '';
      setFieldValue('priceSheet', id);
      setFieldValue('escalationRateMin', '');
      setFieldValue('escalationRateMax', '');
      setFieldValue('minSystemSize', '');
      setFieldValue('maxSystemSize', '');
      setFieldValue('minProductionFactor', '');
      setFieldValue('maxProductionFactor', '');
      if (id) {
        trigger({ id });
      }
    },
    [setFieldValue, trigger],
  );

  const selectedPriceSheetOption = useMemo(
    () => priceSheetOptions.find((option: { value: string }) => option.value === priceSheet) ?? null,
    [priceSheetOptions, priceSheet],
  );

  return (
    <Box childGap="lg">
      <SelectInput
        id="priceSheet"
        name="priceSheet"
        label="Price Sheet"
        value={selectedPriceSheetOption}
        options={priceSheetOptions}
        onChange={handlePriceSheetSelection}
        onBlur={handleBlur}
        isDisabled={isSubmitting}
        error={touched.priceSheet && (errors.priceSheet as string)}
        isRequired
      />
      <Box direction="row" childGap="lg">
        <Field
          name="escalationRateMin"
          id="escalationRateMin"
          label="Escalator Min"
          component={FormikSelectInputNative}
          options={escRateOptions}
          isDisabled={!escRateOptions.length || isSubmitting}
          isRequired
        />
        <Field
          name="escalationRateMax"
          id="escalationRateMax"
          label="Escalator Max"
          component={FormikSelectInputNative}
          options={escRateOptions}
          isDisabled={!escRateOptions.length || isSubmitting}
          isRequired
        />
      </Box>
      <Box direction="row" childGap="lg">
        <Field
          name="minSystemSize"
          id="minSystemSize"
          label="Min System Size"
          component={FormikSelectInputNative}
          options={systemSizeOptions}
          isDisabled={!systemSizeOptions.length || isSubmitting}
          isRequired
        />
        <Field
          name="maxSystemSize"
          id="maxSystemSize"
          label="Max System Size"
          component={FormikSelectInputNative}
          options={systemSizeOptions}
          isDisabled={!systemSizeOptions.length || isSubmitting}
          isRequired
        />
      </Box>
      <Box direction="row" childGap="lg">
        <Field
          name="minProductionFactor"
          id="minProductionFactor"
          label="Min Production Factor"
          component={FormikSelectInputNative}
          options={productionOptions}
          isDisabled={!productionOptions.length || isSubmitting}
          isRequired
        />
        <Field
          name="maxProductionFactor"
          id="maxProductionFactor"
          label="Max Production Factor"
          component={FormikSelectInputNative}
          options={productionOptions}
          isDisabled={!productionOptions.length || isSubmitting}
          isRequired
        />
      </Box>
    </Box>
  );
};
